import React, { useState } from "react";
import { useNavigate } from "react-router";

import { ColumnDef } from "@tanstack/react-table";
import { FlaskConical, Loader2, RefreshCw, XIcon } from "lucide-react";

import {
  embeddingStatusColumn,
  enrichmentStatusColumn,
} from "@/components/common/status-columns";
import { UserAvatar } from "@/components/common/user-avatar";
import { EditButton } from "@/components/refine-ui/buttons/edit";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { makeTooltip } from "@/components/ui/tooltip";
import { useMembers } from "@/contexts/member-context";
import { parseUnixTimestamp } from "@/lib/dateUtils";
import { MergedName } from "@/pages/artists/components/merged-name";
import { useArtistRepository } from "@/pages/artists/hooks/use-artist-repository";
import { IArtistData } from "@/types/artist";

function ArtistRetryButton({ id }: { id: string }) {
  const [loading, setLoading] = useState(false);
  const { triggerEnrich } = useArtistRepository();

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setLoading(true);
    await triggerEnrich(id, {});
    setLoading(false);
  };

  return (
    <Button
      size="sm"
      variant="outline"
      className="h-6 px-2 text-xs gap-1"
      onClick={handleClick}
      disabled={loading}
    >
      {loading ? (
        <Loader2 className="h-3 w-3 animate-spin" />
      ) : (
        <RefreshCw className="h-3 w-3" />
      )}
      Retry
    </Button>
  );
}

export const useArtistsColumns = () => {
  const { members } = useMembers();
  const navigate = useNavigate();
  const columns = React.useMemo<ColumnDef<IArtistData>[]>(
    () => [
      {
        id: "id",
        accessorKey: "document.id",
        header: "ID",
      },
      {
        id: "name",
        // accessorKey: "document.name",
        accessorFn: (row) =>
          row.highlights.filter((h: any) => h.field === "name")[0]?.snippet ??
          row.document.name,
        header: "Name",
        minSize: 100,
        cell: function render({ getValue, row }: any) {
          // The value is a Typesense highlight snippet (<mark>-wrapped), hence the
          // raw HTML. It stays the inner node so MergedName can strike it through
          // without swallowing the highlight.
          return makeTooltip(
            <div className="truncate">
              <MergedName
                mergedInto={row.original?.document?.mergedInto}
                name={<span dangerouslySetInnerHTML={{ __html: getValue() }} />}
              />
            </div>,
          );
        },
        enableSorting: true,
        size: 200,
      },
      {
        id: "hasSpotify",
        accessorKey: "document.metadata.spotifyId",
        header: "Spotify",
        cell: ({ getValue }) => {
          return getValue() ? (
            <Button
              variant={"ghost"}
              onClick={() =>
                window.open(
                  `https://open.spotify.com/artist/${getValue()}`,
                  "_blank",
                )
              }
            >
              <img
                className="h-6 w-6 bg-foreground rounded-full text-green-600"
                src="https://www.svgrepo.com/show/333603/spotify.svg"
              />
            </Button>
          ) : (
            <XIcon className="text-red-700 ml-4" />
          );
        },
        size: 80,
        enableColumnFilter: false,
        enableHiding: false,
        enableSorting: false,
      },
      {
        id: "status",
        header: "Status",
        accessorKey: "document.status",
        cell: function render({ getValue }: any) {
          return <Badge variant="default">{getValue()}</Badge>;
        },
        size: 120,
        enableSorting: false,
        meta: {
          filterType: "select",
          options: [
            { label: "Complete", value: "Complete" },
            { label: "Incomplete", value: "Incomplete" },
            { label: "InReview", value: "InReview" },
            { label: "Unregistered", value: "Unregistered" },
          ],
        },
      },
      // Enrichment + embedding, immediately after Status — the shared
      // definitions. Artist is one of the four EMBEDDABLE entities.
      enrichmentStatusColumn<IArtistData>({
        size: 200,
        actions: ({ status, row }) => {
          if (status === "NeedsReview") {
            return (
              <Button
                size="sm"
                variant="outline"
                className="h-6 px-2 text-xs gap-1"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(
                    `/artists/enrichment/${row.document.id}`,
                    "_blank",
                  );
                }}
              >
                <FlaskConical className="h-3 w-3" />
                Review
              </Button>
            );
          }
          if (status === "Failed") {
            return <ArtistRetryButton id={row.document.id} />;
          }
          return null;
        },
      }),
      embeddingStatusColumn<IArtistData>(),
      {
        id: "genres.name",
        accessorFn: (row) =>
          row?.document?.genres?.map((x) => x.name)?.join(", "),
        cell: function render({ getValue }: any) {
          return makeTooltip(
            <div className="font-bold truncate">{getValue()}</div>,
          );
        },
        header: "Genres",
        enableSorting: false,
      },
      {
        id: "monthlyListeners",
        accessorKey: "document.stats.spotify.monthlyListeners",
        header: "Monthly Listeners",
        size: 100,
        cell: function render({ getValue }: any) {
          return <div className="font-mono">{getValue()}</div>;
        },
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: "followers",
        accessorKey: "document.followers",
        header: "Followers",
        size: 60,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        id: "hide",
        accessorKey: "document.hide",
        header: "Hide",
        size: 60,
        enableSorting: false,
      },
      {
        id: "createdBy",
        header: "Created By",
        minSize: 170,
        accessorKey: "document.createdBy",
        cell: function render({ getValue }: any) {
          let value = getValue();
          const member = members.find(
            (x) =>
              x.id === value || x.adminMetadata?.relatedIds?.includes(value),
          );

          return member ? (
            <div className="p-1">
              <UserAvatar {...member} half />
            </div>
          ) : (
            value
          );
        },
        enableSorting: true,
        meta: {
          filterType: "select",
          options: members.map((m) => ({
            value: m.id,
            label: m.displayName,
          })),
        },
      },
      {
        id: "createdAt",
        header: "Created At",
        accessorFn: (row) => parseUnixTimestamp(row.document.createdAt),
        cell: function render({ getValue }: any) {
          return makeTooltip(
            getValue().format("DD/MM/YYYY HH:mm"),
            getValue().format("ddd, MMM D, YYYY HH:mm"),
          );
        },
        enableColumnFilter: true,
        meta: {
          filterType: "date",
        },
      },
      {
        id: "actions",
        header: "Actions",
        cell: function render({ row }) {
          return (
            <EditButton
              size="sm"
              hideText={true}
              recordItemId={row.original.document.id}
            />
          );
        },
        enableSorting: false,
      },
    ],
    [members, navigate],
  );

  return columns;
};
